import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Mail, Lock, User, ArrowRight, Store } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'user');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, role);
    
    if (result.success) {
      if (role === 'restaurant') {
        navigate('/dashboard');
      } else {
        navigate('/');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="font-extrabold text-3xl text-[#7B2CBF]">
              Zoopbowl
            </span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-[#E8E8E8]">
          <h1 className="text-2xl font-bold text-[#242E30] text-center mb-2">
            Create account
          </h1>
          <p className="text-[#595959] text-center mb-6 text-sm">
            Join Zoopbowl today
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-6 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Account Type */}
            <div>
              <Label className="text-[#242E30] text-sm font-medium mb-3 block">I want to</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                <label 
                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                    role === 'user' 
                      ? 'border-[#7B2CBF] bg-[#7B2CBF]/5' 
                      : 'border-[#E8E8E8] hover:border-[#7B2CBF]'
                  }`}
                >
                  <RadioGroupItem value="user" id="user" className="sr-only" />
                  <User className={`h-5 w-5 ${role === 'user' ? 'text-[#7B2CBF]' : 'text-[#595959]'}`} />
                  <span className={`font-medium text-sm ${role === 'user' ? 'text-[#7B2CBF]' : 'text-[#595959]'}`}>
                    Find deals
                  </span>
                </label>
                <label 
                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                    role === 'restaurant' 
                      ? 'border-[#7B2CBF] bg-[#7B2CBF]/5' 
                      : 'border-[#E8E8E8] hover:border-[#7B2CBF]'
                  }`}
                >
                  <RadioGroupItem value="restaurant" id="restaurant" className="sr-only" />
                  <Store className={`h-5 w-5 ${role === 'restaurant' ? 'text-[#7B2CBF]' : 'text-[#595959]'}`} />
                  <span className={`font-medium text-sm ${role === 'restaurant' ? 'text-[#7B2CBF]' : 'text-[#595959]'}`}>
                    List restaurant
                  </span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="name" className="text-[#242E30] text-sm font-medium">
                {role === 'restaurant' ? 'Your name' : 'Full name'}
              </Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-12"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#242E30] text-sm font-medium">Email address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-12"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#242E30] text-sm font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#595959]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-12"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
              <p className="text-xs text-[#595959] mt-1.5">At least 6 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#7B2CBF] hover:bg-[#5A189A] text-white h-12 font-semibold"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create account'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </form>

          <p className="text-center text-[#595959] text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#7B2CBF] hover:underline font-semibold">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
